import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useRef, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {
  SliceItemDeselected,
  SliceItemSelected,
  SlicerDeselect,
  SlicerHideEmpty,
  SlicerPanelUI,
  SlicerShowEmpty,
} from '../../api/stats/contracts'
import {type SliceValue, useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import {BulkAddItemsProvider} from '../side-panel/bulk-add/bulk-add-items-provider'
import {SliceByMenu} from '../slice-by-menu'
import {SLICER_PANEL_DEFAULT_WIDTH, SLICER_PANEL_WIDE} from './constants'
import {SlicerGroupByItems, SlicerIterationItems} from './slicer-items'
import itemStyles from './slicer-items.module.css'
import type {SlicerItemGroup} from './slicer-items-provider'
import styles from './slicer-panel.module.css'
import {SlicerPanelResizer} from './slicer-panel-resizer'

export function SlicerPanel({slicerItems}: {slicerItems: Array<SlicerItemGroup> | null}) {
  const {sliceField, sliceValue, setSliceValue, sliceByPanelWidth, setSliceByPanelWidth} = useSliceBy()
  const [isSliceByMenuOpen, setIsSliceByMenuOpen] = useState(false)
  const [showEmptyItems, setShowEmptyItems] = useState(false)
  const {currentView} = useViews()
  const {viewType} = useViewType()
  const {postStats} = usePostStats()
  const {hasWritePermissions} = ViewerPrivileges()

  const [localPanelWidth, setLocalPanelWidth] = useState<Record<number, number>>({})

  const anchorRef = useRef<HTMLButtonElement>(null)

  const toggleSliceValue = useCallback(
    (value: SliceValue) => {
      if (!sliceField || !currentView) return

      const toggleOn = value !== sliceValue

      setSliceValue(currentView.number, toggleOn ? value : null)

      postStats({
        name: toggleOn ? SliceItemSelected : SliceItemDeselected,
        memexProjectColumnId: sliceField.id,
        ui: SlicerPanelUI,
        context: JSON.stringify({layout: viewType}),
      })
    },
    [sliceField, currentView, sliceValue, setSliceValue, postStats, viewType],
  )

  const deselectSelection = useCallback(() => {
    if (!sliceField || !currentView) return

    setSliceValue(currentView.number, null)

    postStats({
      name: SlicerDeselect,
      memexProjectColumnId: sliceField.id,
      ui: SlicerPanelUI,
      context: JSON.stringify({layout: viewType}),
    })
  }, [sliceField, currentView, setSliceValue, postStats, viewType])

  const showEmptySlicerItems = useCallback(
    (show: boolean) => {
      if (!sliceField || !currentView) return

      setShowEmptyItems(show)

      postStats({
        name: show ? SlicerShowEmpty : SlicerHideEmpty,
        memexProjectColumnId: sliceField.id,
        ui: SlicerPanelUI,
        context: JSON.stringify({layout: viewType}),
      })
    },
    [sliceField, currentView, postStats, viewType],
  )

  const renderSlicerItems = useCallback(() => {
    if (!sliceField || !slicerItems) return null

    switch (sliceField.dataType) {
      case MemexColumnDataType.Iteration:
        return (
          <SlicerIterationItems
            sliceValue={sliceValue}
            onSliceValueChange={toggleSliceValue}
            slicerItems={slicerItems}
            showEmptySlicerItems={showEmptyItems}
            setShowEmptySlicerItems={showEmptySlicerItems}
          />
        )
      default:
        return (
          <SlicerGroupByItems
            sliceValue={sliceValue}
            onSliceValueChange={toggleSliceValue}
            slicerItems={slicerItems}
            showEmptySlicerItems={showEmptyItems}
            setShowEmptySlicerItems={showEmptySlicerItems}
          />
        )
    }
  }, [sliceField, slicerItems, sliceValue, toggleSliceValue, showEmptyItems, showEmptySlicerItems])

  // Handle resizing the slicer panel while the user is dragging the resizer control
  const handleResize = useCallback(
    (width: number) => {
      if (!currentView) return

      setLocalPanelWidth(prev => {
        return {
          ...prev,
          [currentView.number]: width,
        }
      })
    },
    [currentView, setLocalPanelWidth],
  )

  // Handle resizing the slicer panel when the user has stopped
  // dragging the resizer control or has double clicked it
  // This will update the slice by panel width server side
  const handleResizeEnd = useCallback(
    (width: number) => {
      if (!currentView) return

      setLocalPanelWidth(prev => {
        const newState = {...prev}
        delete newState[currentView.number]

        return newState
      })

      setSliceByPanelWidth(width)
    },
    [currentView, setSliceByPanelWidth],
  )

  if (!currentView) return null

  const panelWidth = localPanelWidth[currentView.number] ?? sliceByPanelWidth ?? SLICER_PANEL_DEFAULT_WIDTH

  const panelClassNames = clsx(itemStyles.SlicerPanel, {
    [itemStyles.Wide]: panelWidth > SLICER_PANEL_WIDE,
    [itemStyles.WritePermissions]: !hasWritePermissions,
  })

  return (
    /**
     * The height: 0 and minHeight: '100%' trickery is needed to allow scrolling the slicer panel
     * within the grid container. Without this, the slicer panel will not scroll correctly.
     *
     * See: https://stackoverflow.com/a/43312314
     * See: https://stackoverflow.com/a/43352777
     */
    <div className={styles.Box}>
      <div
        {...testIdProps('slicer-panel')}
        className={panelClassNames}
        style={{
          width: `${panelWidth}px`,
        }}
      >
        <BulkAddItemsProvider>
          {sliceField && slicerItems ? (
            <>
              <div className={styles.Box_1}>
                <Button
                  variant="invisible"
                  ref={anchorRef}
                  aria-expanded={isSliceByMenuOpen}
                  onClick={() => setIsSliceByMenuOpen(!isSliceByMenuOpen)}
                  trailingVisual={TriangleDownIcon}
                  className={styles.Button}
                >
                  <Truncate
                    {...testIdProps('slicer-panel-title')}
                    id="slicer-panel-title"
                    title={sliceField.name}
                    className={styles.Truncate}
                  >
                    {sliceField.name}
                  </Truncate>
                </Button>
                {sliceValue && (
                  <Button variant="invisible" onClick={deselectSelection} size="small" className={styles.Button_1}>
                    Deselect
                  </Button>
                )}
              </div>
              <SliceByMenu
                anchorRef={anchorRef}
                open={isSliceByMenuOpen}
                setOpen={setIsSliceByMenuOpen}
                ui={SlicerPanelUI}
              />

              {renderSlicerItems()}
            </>
          ) : null}
        </BulkAddItemsProvider>
      </div>
      {hasWritePermissions && (
        <SlicerPanelResizer onResize={handleResize} onResizeEnd={handleResizeEnd} width={panelWidth} />
      )}
    </div>
  )
}

try{ SlicerPanel.displayName ||= 'SlicerPanel' } catch {}